import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LocalAuthService, UserService } from '@sso/ld-sso-with-oauth2-oidc';
import {
  BehaviorSubject,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  of,
  shareReplay,
  Subscription,
  switchMap,
  throttleTime,
  timer,
} from 'rxjs';
import { hasRmDemoRole, Role } from '../enums/role.enum';
import {
  IdleWarningDialogComponent,
  WARNING_DURATION_SECONDS,
} from '../../shared/components/idle-warning-dialog/idle-warning-dialog.component';
import { ShowcaseSessionService } from './showcase-session.service';
import { WsDemoConnectorService } from './ws-demo-connector.service';

// Fallback only: used when the initial lease lookup fails transiently (e.g. network
// hiccup on boot), so the client still has something to count down from. Once a real
// `leasedUntil` is known, it is always the source of truth. Deliberately shorter than
// the backend's actual lease duration (currently 5 minutes): guessing too long risks
// an activity-triggered renewal hitting an already-expired lease (401, hard logout via
// the global auth interceptor) before the local timer notices anything is wrong;
// guessing too short just shows the warning dialog a bit early, which is harmless.
const IDLE_TIMEOUT_SECONDS = 3 * 60;

// How often a genuine user interaction is allowed to trigger a renewShowcaseLease call.
// The backend deliberately has no heartbeat - renewal is an explicit holder action - so
// this throttles repeated interactions rather than polling on a fixed interval.
const RENEW_THROTTLE_MS = 60 * 1000;

// How often a group member's session is polled for continued validity - see
// ShowcaseSessionService.checkGroupSessionAlive(). A fallback on top of the
// push-based usageEnded$ watch below - a group member gets both.
const GROUP_HEARTBEAT_INTERVAL_MS = 10 * 1000;

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private _localAuthService = inject(LocalAuthService);
  private _userService = inject(UserService);
  private _dialog = inject(MatDialog);
  private _ngZone = inject(NgZone);
  private _showcaseSessionService = inject(ShowcaseSessionService);
  private _wsDemoConnectorService = inject(WsDemoConnectorService);

  private isActive = false;
  private activitySubscription?: Subscription;
  private tickSubscription?: Subscription;
  private usageEndedSubscription?: Subscription;
  private heartbeatSubscription?: Subscription;
  private warningDialogRef?: MatDialogRef<IdleWarningDialogComponent>;
  private leasedUntil: Date | null = null;
  private readonly _remainingSeconds$ = new BehaviorSubject<number | null>(
    null,
  );

  private readonly user$ = this._localAuthService.isAuthenticated$.pipe(
    distinctUntilChanged(),
    switchMap((isAuthenticated) =>
      isAuthenticated ? this._userService.getUserInfo$() : of(null),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  isShowcase$ = this.user$.pipe(
    map((user) => hasRmDemoRole(user?.info?.roles ?? [])),
  );

  // Only the holder (RM_DEMO) has a lease of their own - fetchCurrentLease,
  // renewLease and the freeShowcase call inside logout() are all holder-only
  // server-side and reject a group member (RM_DEMO_GROUP) with 401. So unlike
  // isShowcase$ above (used elsewhere to gate showcase-wide UI/behavior for
  // both roles), this lease-tracking mechanism only ever watches for the holder.
  private readonly isHolder$ = this.user$.pipe(
    map((user) => (user?.info?.roles ?? []).includes(Role.RM_DEMO)),
  );

  // A group member has no lease of their own to watch; startHeartbeat() below
  // polls their session's continued validity as a fallback on top of the
  // usageEnded$ watch that isShowcase$ already sets up for both roles.
  private readonly isGroupMember$ = this.user$.pipe(
    map((user) => (user?.info?.roles ?? []).includes(Role.RM_DEMO_GROUP)),
  );

  /** Seconds until automatic logout due to inactivity; null while not applicable (e.g. not the showcase holder). */
  readonly remainingSeconds$ = this._remainingSeconds$.asObservable();

  constructor() {
    this.isHolder$.pipe(distinctUntilChanged()).subscribe((isHolder) => {
      this.isActive = isHolder;
      if (isHolder) {
        this.startWatching();
      } else {
        this.teardown();
      }
    });

    // Both roles (holder and group member) end their session the moment the demo
    // feed says the usage is over - for the holder it's on top of the lease
    // countdown above (the usage can end server-side before their own lease
    // would have expired); for a group member it's on top of the heartbeat
    // poll below (see isGroupMember$).
    this.isShowcase$
      .pipe(distinctUntilChanged())
      .subscribe((isShowcase) => {
        if (isShowcase) {
          this.startUsageEndedWatch();
        } else {
          this.stopUsageEndedWatch();
        }
      });

    this.isGroupMember$
      .pipe(distinctUntilChanged())
      .subscribe((isGroupMember) => {
        if (isGroupMember) {
          this.startHeartbeat();
        } else {
          this.stopHeartbeat();
        }
      });
  }

  logout(): void {
    this._showcaseSessionService
      .endSession()
      .subscribe(() => this._localAuthService.logout());
  }

  /** (Re)syncs the lease from the backend, then starts counting down from it. */
  private startWatching(): void {
    this._showcaseSessionService.fetchCurrentLease().subscribe((result) => {
      this._ngZone.run(() => {
        if (!this.isActive) {
          return;
        }

        if (result.status === 'expired') {
          this.logout();
          return;
        }

        // 'unknown' (e.g. a transient network error on boot) falls back to the local
        // default so the countdown still works; it self-corrects on the next renewal.
        this.watch(
          result.status === 'renewed'
            ? result.leasedUntil
            : new Date(Date.now() + IDLE_TIMEOUT_SECONDS * 1000),
        );
      });
    });
  }

  private watch(leasedUntil: Date): void {
    this.leasedUntil = leasedUntil;
    this.startTicking();
    this.startActivityListening();
  }

  private startTicking(): void {
    this.tickSubscription?.unsubscribe();

    this._ngZone.runOutsideAngular(() => {
      this.tickSubscription = timer(0, 1000).subscribe(() => {
        this._ngZone.run(() => {
          const remainingSeconds = this.leasedUntil
            ? Math.round((this.leasedUntil.getTime() - Date.now()) / 1000)
            : 0;
          this._remainingSeconds$.next(Math.max(remainingSeconds, 0));

          // The warning dialog must open *before* the real backend deadline, with the
          // same lead time it displays as its own countdown - otherwise "still here"
          // clicks land after the lease already died server-side and get rejected
          // with 401, which the app's global auth interceptor turns into an abrupt
          // hard logout instead of the graceful flow below.
          if (remainingSeconds <= 0) {
            this.activitySubscription?.unsubscribe();
            this.tickSubscription?.unsubscribe();
            this.logout();
          } else if (remainingSeconds <= WARNING_DURATION_SECONDS) {
            this.showWarning();
          }
        });
      });
    });
  }

  /** Renews the lease on genuine (throttled) interaction - not a heartbeat, the backend has none. */
  private startActivityListening(): void {
    this.activitySubscription?.unsubscribe();

    this._ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'touchstart'),
      );

      this.activitySubscription = activity$
        .pipe(
          throttleTime(RENEW_THROTTLE_MS),
          switchMap(() => this._showcaseSessionService.renewLease()),
        )
        .subscribe((result) =>
          this._ngZone.run(() => {
            if (result.status === 'expired') {
              this.logout();
            } else if (result.status === 'renewed') {
              this.leasedUntil = result.leasedUntil;
            }
            // 'unknown': keep the previous leasedUntil, retried on the next interaction.
          }),
        );
    });
  }

  private startUsageEndedWatch(): void {
    this.stopUsageEndedWatch();

    this.usageEndedSubscription = this._wsDemoConnectorService.usageEnded$.subscribe(
      () => this._ngZone.run(() => this.logout()),
    );
  }

  private stopUsageEndedWatch(): void {
    this.usageEndedSubscription?.unsubscribe();
    this.usageEndedSubscription = undefined;
  }

  /** Polls a group member's session and logs them out once it's no longer valid. */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this._ngZone.runOutsideAngular(() => {
      this.heartbeatSubscription = timer(
        GROUP_HEARTBEAT_INTERVAL_MS,
        GROUP_HEARTBEAT_INTERVAL_MS,
      )
        .pipe(switchMap(() => this._showcaseSessionService.checkGroupSessionAlive()))
        .subscribe((isAlive) => {
          if (!isAlive) {
            this._ngZone.run(() => this.logout());
          }
        });
    });
  }

  private stopHeartbeat(): void {
    this.heartbeatSubscription?.unsubscribe();
    this.heartbeatSubscription = undefined;
  }

  private teardown(): void {
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.tickSubscription?.unsubscribe();
    this.tickSubscription = undefined;
    this.leasedUntil = null;
    this._remainingSeconds$.next(null);
    this.warningDialogRef?.close();
  }

  private showWarning(): void {
    if (this.warningDialogRef) {
      return;
    }

    this.activitySubscription?.unsubscribe();
    this.tickSubscription?.unsubscribe();

    this.warningDialogRef = this._dialog.open(IdleWarningDialogComponent, {
      disableClose: true,
    });

    this.warningDialogRef.afterClosed().subscribe((confirmed) => {
      this.warningDialogRef = undefined;

      if (!this.isActive) {
        return;
      }

      if (!confirmed) {
        this.logout();
        return;
      }

      // Confirming "still here" must renew the lease, bypassing the throttle - if the
      // backend can't confirm the lease is still alive, logging out is the safe default.
      this._showcaseSessionService.renewLease().subscribe((result) =>
        this._ngZone.run(() => {
          if (result.status === 'renewed') {
            this.watch(result.leasedUntil);
          } else {
            this.logout();
          }
        }),
      );
    });
  }
}
